import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import DealCard from "@/components/DealCard";
import SaleMarquee from "@/components/SaleMarquee";
import Seo from "@/components/Seo";
import { ArrowLeft } from "@phosphor-icons/react";

export default function CategoryPage() {
  const { slug, catslug } = useParams();
  const tenantSlug = catslug ? slug : null;
  const categorySlug = catslug || slug;
  const backTo = tenantSlug ? `/site/${tenantSlug}` : "/";
  const [links, setLinks] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [cats, l] = await Promise.all([
      api.get("/categories", { params: tenantSlug ? { tenant: tenantSlug } : {} }),
      api.get("/links", { params: { category: categorySlug, ...(tenantSlug ? { tenant: tenantSlug } : {}) } }),
    ]);
    setCategory(cats.data.find((c) => c.slug === categorySlug) || null);
    setLinks(l.data);
    setLoading(false);
  }, [categorySlug, tenantSlug]);

  useEffect(() => { load(); }, [load]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const catName = category ? category.name : categorySlug?.replace(/-/g, " ");
  const catPath = tenantSlug ? `/site/${tenantSlug}/category/${categorySlug}` : `/category/${categorySlug}`;
  const itemListLd = links.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${catName} deals`,
    itemListElement: links.map((l, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${origin}/deal/${l.id}`,
      name: l.title,
    })),
  } : null;
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
      { "@type": "ListItem", position: 2, name: catName, item: `${origin}${catPath}` },
    ],
  };

  return (
    <div>
      <Seo
        title={`${catName} Deals`}
        description={category?.description || `Browse the best ${categorySlug?.replace(/-/g, " ")} affiliate deals — real prices, real reviews. ${links.length} hand-picked offers on Affiliated Connections.`}
        keywords={`${categorySlug?.replace(/-/g, " ")} deals, affiliate links, offers, discounts`}
        path={catPath}
        jsonLd={itemListLd ? [itemListLd, breadcrumbLd] : [breadcrumbLd]}
      />
      <div className="relative bg-[#18181B] border-b border-zinc-800 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to={backTo} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#00FFFF] mb-4">
            <ArrowLeft size={16} /> All Deals
          </Link>
          <h1 className="font-display text-4xl sm:text-6xl font-black uppercase tracking-tighter">
            {category ? category.name : categorySlug}
          </h1>
          {category?.description && <p className="text-zinc-400 mt-3 max-w-2xl">{category.description}</p>}
          <p className="font-mono text-sm text-[#FF007F] mt-4">{links.length} deals blazing</p>
        </div>
      </div>

      <SaleMarquee />

      <section className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <p className="text-zinc-500 font-mono">Loading…</p>
        ) : links.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-12 text-center text-zinc-400">
            No deals in this category yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {links.map((l, i) => <DealCard key={l.id} link={l} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
